import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart,
  BarChart,
  Bar
} from 'recharts';
import { motion } from 'framer-motion';
import { ArrowUpRight, BarChart2, LineChart as LineChartIcon, PieChart } from 'lucide-react';
import type { WidgetProps } from '../../../types/dashboard';

type TimePeriod = '1D' | '1W' | '1M' | '1Y' | 'ALL';
type ChartType = 'line' | 'area' | 'bar';

const generateData = (period: TimePeriod) => {
  const now = new Date();
  const data = [];
  let points = 0;
  let interval = 0;
  
  switch (period) {
    case '1D':
      points = 24;
      interval = 60 * 60 * 1000; // 1 hour
      break;
    case '1W':
      points = 7;
      interval = 24 * 60 * 60 * 1000; // 1 day
      break;
    case '1M':
      points = 30;
      interval = 24 * 60 * 60 * 1000; // 1 day
      break;
    case '1Y':
      points = 12;
      interval = 30 * 24 * 60 * 60 * 1000; // ~1 month
      break;
    case 'ALL':
      points = 24;
      interval = 30 * 24 * 60 * 60 * 1000; // ~1 month
      break;
  }

  for (let i = points - 1; i >= 0; i--) {
    const date = new Date(now.getTime() - (i * interval));
    const value = Math.floor(2000 + Math.random() * 2000);
    const engagement = Math.floor(15 + Math.random() * 10);
    const clicks = Math.floor(value * 0.05);
    
    let name = '';
    switch (period) {
      case '1D':
        name = date.toLocaleTimeString([], { hour: '2-digit' });
        break;
      case '1W':
        name = date.toLocaleDateString([], { weekday: 'short' });
        break;
      case '1M':
        name = date.toLocaleDateString([], { day: 'numeric' });
        break;
      case '1Y':
      case 'ALL':
        name = date.toLocaleDateString([], { month: 'short' });
        break;
    }

    data.push({ name, value, engagement, clicks });
  }

  return data;
};

export const AnalyticsWidget: React.FC<WidgetProps> = () => {
  const [period, setPeriod] = useState<TimePeriod>('1W');
  const [data, setData] = useState(() => generateData('1W'));
  const [chartType, setChartType] = useState<ChartType>('area');

  const handlePeriodChange = (newPeriod: TimePeriod) => {
    setPeriod(newPeriod);
    setData(generateData(newPeriod));
  };

  const renderChart = () => {
    switch (chartType) {
      case 'line':
        return (
          <LineChart data={data} margin={{ top: 10, right: 10, left: -15, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              width={40}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                padding: '0.75rem',
              }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#6366f1"
              strokeWidth={2}
              dot={false}
              name="Views"
            />
            <Line
              type="monotone"
              dataKey="engagement"
              stroke="#22c55e"
              strokeWidth={2}
              dot={false}
              name="Engagement"
            />
          </LineChart>
        );
      case 'area':
        return (
          <AreaChart data={data} margin={{ top: 10, right: 10, left: -15, bottom: 10 }}>
            <defs>
              <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#6366f1" stopOpacity={0.1}/>
              </linearGradient>
              <linearGradient id="colorEngagement" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              width={40}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                padding: '0.75rem',
              }}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="value"
              stroke="#6366f1"
              fillOpacity={1}
              fill="url(#colorViews)"
              name="Views"
            />
            <Area
              type="monotone"
              dataKey="engagement"
              stroke="#22c55e"
              fillOpacity={1}
              fill="url(#colorEngagement)"
              name="Engagement"
            />
          </AreaChart>
        );
      case 'bar':
        return (
          <BarChart data={data} margin={{ top: 10, right: 10, left: -15, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              width={40}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                padding: '0.75rem',
              }}
            />
            <Legend />
            <Bar dataKey="value" fill="#6366f1" name="Views" radius={[4, 4, 0, 0]} />
            <Bar dataKey="clicks" fill="#22c55e" name="Clicks" radius={[4, 4, 0, 0]} />
          </BarChart>
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-2">
          <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-md">
            <BarChart2 className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">Performance Overview</div>
        </div>
        <Link to="/performance" className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center hover:underline">
          Full Report <ArrowUpRight className="ml-1 h-3 w-3" />
        </Link>
      </div>

      <div className="flex items-center justify-between mb-5">
        <div className="flex space-x-1 bg-gray-100 dark:bg-dark-700 p-1 rounded-lg">
          {(['line', 'area', 'bar'] as ChartType[]).map((type) => {
            const Icon = type === 'line' ? LineChartIcon : type === 'area' ? LineChartIcon : BarChart2;
            return (
              <button
                key={type}
                onClick={() => setChartType(type)}
                className={`p-2 rounded-md text-xs flex items-center justify-center transition-colors ${
                  chartType === type
                    ? 'bg-white dark:bg-dark-800 text-indigo-600 dark:text-indigo-400 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
              </button>
            );
          })}
        </div>
        <div className="flex space-x-1 bg-gray-100 dark:bg-dark-700 p-1 rounded-lg">
          {(['1D', '1W', '1M', '1Y'] as TimePeriod[]).map((p) => (
            <motion.button
              key={p}
              onClick={() => handlePeriodChange(p)}
              className={`px-2.5 py-1.5 text-xs rounded-md transition-colors ${
                period === p
                  ? 'bg-white dark:bg-dark-800 text-indigo-600 dark:text-indigo-400 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {p}
            </motion.button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-[200px] border border-gray-100 dark:border-dark-700 rounded-xl p-3">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-3 gap-4 mt-5">
        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">Total Views</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white mt-1.5">128.5K</div>
          <div className="text-xs text-green-600 dark:text-green-400 mt-1.5">+8.1% vs last period</div>
        </div>
        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">Engagement Rate</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white mt-1.5">4.3%</div>
          <div className="text-xs text-green-600 dark:text-green-400 mt-1.5">+2.4% vs last period</div>
        </div>
        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400">Conversion</div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white mt-1.5">2.8%</div>
          <div className="text-xs text-green-600 dark:text-green-400 mt-1.5">+1.1% vs last period</div>
        </div>
      </div>
    </div>
  );
};